<template>
  <div :id="id" class="users-multiselect">
    <AutoComplete
      ref="autocomplete"
      v-model="selectedUsers"
      :suggestions="filteredUsers"
      @complete="search"
      @change="onSelectionChange"
      :completeOnFocus="true"
      :loading="false"
      :forceSelection="!allowCreate || undefined"
      :disabled="disabled || undefined"
      :multiple="maxItems > 1"
      optionLabel="text"
      @keydown.esc.stop
      @keydown="handleKeyDown"
      v-bind="$attrs">

      <!-- Custom option template -->
      <template #option="{ option }">
        <div class="user-option">
          <div class="user-option-header font-bold">
            <span class="user-name">{{ option.name }}</span>
            <span class="user-id"> ({{ option.id }})</span>
          </div>
          <div class="user-email text-gray-500 text-xs">{{ option.email }}</div>
        </div>
      </template>
    </AutoComplete>

    <select :name="name" :multiple="maxItems > 1" class="hidden" ref="hiddenSelect">
      <option v-for="item in selectedUsersArray" :key="item.value" :value="item.value" selected></option>
    </select>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
  import AutoComplete from 'primevue/autocomplete'
  import axios from 'axios'

  const props = defineProps({
    id:                     { type: String, required: true },
    name:                   { type: String, required: true },
    usersUrl:               { type: String, default: '/admin/users/accounts/users.json' },
    initialSelectedValues:  { type: Array, default: () => [] },
    maxItems:               { type: Number, default: 1000 },
    allowCreate:            { type: Boolean, default: false },
    disabled:               { type: Boolean, default: false },
    groupIds:               { type: [String, Array], default: null },
    programIds:             { type: [String, Array], default: null },
    sessionIds:             { type: [String, Array], default: null },
    staticOptions:          { type: Array, default: () => [] },
    mode:                   { type: String, default: 'id', validator: (value) => ['id', 'email'].includes(value) }
  })

  /*
   Example of staticOptions:
    @pair_match.possible_mentors.values.map { |mentor| { 
      id: mentor.id,
      name: mentor.name,
      email: mentor.email
    } }
  */

  const id = props.id
  const name = props.name + ((props.maxItems > 1) ? '[]' : '')

  const users = ref([])
  const loading = ref(false)
  const filteredUsers = ref([])
  const hiddenSelect = ref(null)
  const autocomplete = ref(null)

  const forceSelection = !props.allowCreate

  const transformUser = (user) => ({
    text: user.name,
    value: props.mode === 'email' ? user.email : user.id,
    id: user.id,
    name: user.name,
    email: user.email
  })

  // Transform users data to the format expected by AutoComplete
  const transformedUsers = computed(() => {
    // If static options provided, use those instead of fetched users
    if (props.staticOptions.length > 0) {
      return props.staticOptions
    }
    return users.value.map(transformUser)
  })


  // Initialize selectedUsers with mapped objects
  const selectedUsers = ref(
    props.initialSelectedValues
      .map(value => {
        const user = users.value.find(u => props.mode === 'email' ? u.email === value : u.id === value)
        return user ? transformUser(user) : null
      })
      .filter(item => item !== null)
  )

  const selectedUsersArray = computed(() => {
    if (!selectedUsers.value) return [];
    return Array.isArray(selectedUsers.value) ? selectedUsers.value : [selectedUsers.value]
  })

  // Load users from API
  const loadUsers = async (query = '') => {
    // Add this check at the very beginning
    if (!props.usersUrl || props.usersUrl === '' || props.usersUrl === 'null') {
      loading.value = false
      users.value = [] // Clear any existing users
      filteredUsers.value = [] // Clear filtered results
      return
    }
    
    loading.value = true
    try {
      const params = { q: query }

      // Add optional filter parameters
      if (props.groupIds)   { params.g = Array.isArray(props.groupIds) ? props.groupIds.join(',') : props.groupIds }
      if (props.programIds) { params.p = Array.isArray(props.programIds) ? props.programIds.join(',') : props.programIds }
      if (props.sessionIds) { params.s = Array.isArray(props.sessionIds) ? props.sessionIds.join(',') : props.sessionIds }

      const response = await axios.get(props.usersUrl, { params })
      users.value = response.data
      filteredUsers.value = transformedUsers.value
    } catch (error) {
      console.error('Error loading users:', error)
      users.value = []
      filteredUsers.value = []
    } finally {
      loading.value = false
    }
  }

  // Handle search/filtering
  const search = (event) => {
    const queryLower = event.query.toLowerCase()

    filteredUsers.value = transformedUsers.value.filter(user => {
      // const notSelected = !selectedUsersArray.value.some(selected => selected.value?.toString() === user.value?.toString())

      const matchesQuery =
        user.name.toLowerCase().includes(queryLower) ||
        user.email.toLowerCase().includes(queryLower) ||
        user.id.toString().includes(queryLower)

      // return notSelected && matchesQuery
      return matchesQuery
    })
  }

  // Update users URL and reload
  const updateUsersUrl = (newUrl) => {
    // This could be enhanced to actually update the URL prop
    loadUsers()
  }

  // Reload functionality
  const reload = () => {
    users.value = []
    selectedUsers.value = []
    filteredUsers.value = []
    loadUsers()
  }

  const handleKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      // Don't show dropdown if we've reached maxItems
      if (selectedUsersArray.value.length >= props.maxItems) {
        return
      }

      // Trigger the search to show all items
      if (filteredUsers.value.length === 0) {
        search({ query: '' });
      }

      nextTick(() => {
        if (autocomplete.value && autocomplete.value.show) {
          autocomplete.value.show();
        }
      });
    }
  }

  const onSelectionChange = () => {
    if (props.maxItems > 1) {
      search({ query:  '' })
    }
  }

  // Handle external option updates (maintaining compatibility with existing Multiselect)
  const updateOptions = (event) => {
    users.value = event.detail.map(item => ({
      id: item.value,
      name: item.text,
      email: item.email || '',
    }))
    search({ query: '' })
  }

  // Watch for changes to users and update selectedUsers if needed
  watch(users, (newUsers) => {
    if (newUsers.length && props.initialSelectedValues.length && !selectedUsers.value.length) {
      selectedUsers.value = props.initialSelectedValues
      .map(value => {
        const user = users.value.find(u => props.mode === 'email' ? u.email === value : u.id === value)
        return user ? transformUser(user) : null
      })
      .filter(item => item !== null)
    }
  }, { immediate: true })

  // Expose methods for external access
  defineExpose({
    reload,
    updateUsersUrl
  })

  // Propagate the change event to the hidden select
  watch(selectedUsers, () => {
    nextTick(() => {
      if (hiddenSelect.value) {
        hiddenSelect.value.dispatchEvent(new Event('change', { bubbles: true }))
      }
    })
  }, { deep: true })

  onMounted(() => {
    const el = document.getElementById(id)
    if (el) el.addEventListener('setOptions', updateOptions)
    
    // Use static options if provided
    if (props.staticOptions.length > 0) {
      users.value = props.staticOptions
      filteredUsers.value = transformedUsers.value
    } else if (props.usersUrl && props.usersUrl !== '') {
      loadUsers()
    }
  })

  onUnmounted(() => {
    const el = document.getElementById(id)
    if (el) el.removeEventListener('setOptions', updateOptions)
  })
</script>
